/**
 * In-app "Test App" runner.
 *
 * Drives the REAL application against the REAL backend (Firestore + the
 * deployed Cloud Functions) exactly as a user would — but inside a disposable
 * "Test Sandbox" workspace so it never touches real data. Every journey below
 * mirrors a real user task; the runner deploys a pipeline, performs an action,
 * and waits for the server-side automation to produce the promised result.
 *
 * Safety model:
 *  - All test data lives in workspaces whose name starts with SANDBOX_PREFIX.
 *  - deleteAllTestData() removes those workspaces and the inbox notifications
 *    they produced, so "completely delete the tests" is one call.
 *
 * Admin-only: surfaced in Settings only for application admins.
 */
import { dataService } from './dataService';
import { getInboxItems, deleteInboxItem } from '../utils/inboxStorage';

export const SANDBOX_PREFIX = '🧪 Test Sandbox';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Poll the live inbox (subscribed to users/{uid}/inbox) for an item belonging
// to this sandbox workspace. Returns the item, or null on timeout.
async function waitForInbox(workspaceId, { timeoutMs = 25000, match } = {}) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const hit = getInboxItems().find(
      (it) => it.workspaceId === workspaceId && (!match || match(it))
    );
    if (hit) return hit;
    await sleep(1500);
  }
  return null;
}

// Poll a collection's records until `predicate` holds, or timeout.
async function waitForRecord(workspaceId, collectionId, predicate, timeoutMs = 25000) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const recs = await dataService.getRecords(workspaceId, collectionId);
    const hit = recs.find(predicate);
    if (hit) return hit;
    await sleep(1500);
  }
  return null;
}

// ── Pipeline builders (small, self-contained) ────────────────────────────────

function contactsPipeline() {
  const nodes = [
    { id: 'tc1', type: 'collectionNode', position: { x: 80, y: 160 }, data: {
      label: 'Contacts',
      fields: [
        { id: 'f1', name: 'Name', type: 'text' },
        { id: 'f2', name: 'Email', type: 'text' },
      ],
    } },
    { id: 'tt1', type: 'triggerNode', position: { x: 340, y: 160 }, data: {
      label: 'New Contact', triggerType: 'record_added', triggerCollectionId: 'tc1',
    } },
    { id: 'ta1', type: 'actionNode', position: { x: 600, y: 160 }, data: {
      label: 'Notify', actionType: 'send_notification',
      notificationSubject: 'Test: new contact {name}',
      notificationText: 'A new contact {name} ({email}) was added. Pipeline ran successfully.',
    } },
  ];
  const edges = [
    { id: 'te1', source: 'tc1', target: 'tt1' },
    { id: 'te2', source: 'tt1', target: 'ta1' },
  ];
  return { nodes, edges };
}

function ticketsStatusPipeline() {
  const nodes = [
    { id: 'sc1', type: 'collectionNode', position: { x: 80, y: 160 }, data: {
      label: 'Tickets',
      fields: [
        { id: 'f1', name: 'Title', type: 'text' },
        { id: 'f2', name: 'Status', type: 'select', options: ['Open', 'Done'] },
      ],
    } },
    { id: 'st1', type: 'triggerNode', position: { x: 340, y: 160 }, data: {
      label: 'New Ticket', triggerType: 'record_added', triggerCollectionId: 'sc1',
    } },
    { id: 'sa1', type: 'actionNode', position: { x: 600, y: 160 }, data: {
      label: 'Set Status to Open', actionType: 'update_field',
      collectionId: 'sc1', fieldId: 'f2', fieldName: 'Status', fieldValue: 'Open',
    } },
  ];
  const edges = [
    { id: 'se1', source: 'sc1', target: 'st1' },
    { id: 'se2', source: 'st1', target: 'sa1' },
    { id: 'se3', source: 'sa1', target: 'sc1' }, // action writes back to the collection
  ];
  return { nodes, edges };
}

// ── Journeys ─────────────────────────────────────────────────────────────────
// Each step receives a shared mutable ctx ({ workspaceId, ... }) and returns
// { ok, detail }. Keep step labels in plain English — they appear in the report.

export const JOURNEYS = [
  {
    id: 'notify',
    name: 'Adding a record sends a notification',
    summary: 'Deploys a Contacts pipeline, adds a contact, and checks the Inbox notification arrives.',
    steps: [
      { label: 'Deploy the Contacts pipeline and activate it', run: async (ctx) => {
        const { nodes, edges } = contactsPipeline();
        await dataService.deployPipeline(ctx.workspaceId, nodes, edges, true);
        return { ok: true, detail: 'Pipeline deployed and turned on.' };
      } },
      { label: 'Add a contact record', run: async (ctx) => {
        await dataService.addRecord(ctx.workspaceId, 'tc1', { f1: 'Test User', f2: 'test@example.com' });
        return { ok: true, detail: 'Added contact "Test User".' };
      } },
      { label: 'A notification appears in the Inbox', run: async (ctx) => {
        const item = await waitForInbox(ctx.workspaceId, { match: (it) => /test user/i.test(`${it.subject} ${it.body}`) });
        return item
          ? { ok: true, detail: `Inbox received: "${item.subject}".` }
          : { ok: false, detail: 'No notification arrived within 25s.' };
      } },
    ],
  },
  {
    id: 'status',
    name: 'A new ticket is auto-set to "Open"',
    summary: 'Deploys a Tickets pipeline and checks the status-update action runs server-side.',
    steps: [
      { label: 'Deploy the Tickets pipeline and activate it', run: async (ctx) => {
        const { nodes, edges } = ticketsStatusPipeline();
        await dataService.deployPipeline(ctx.workspaceId, nodes, edges, true);
        return { ok: true, detail: 'Pipeline deployed and turned on.' };
      } },
      { label: 'Add a ticket with a blank status', run: async (ctx) => {
        const rec = await dataService.addRecord(ctx.workspaceId, 'sc1', { f1: 'Fix login bug', f2: '' });
        ctx.ticketId = rec?.id;
        return { ok: true, detail: 'Added ticket "Fix login bug" (Status blank).' };
      } },
      { label: 'The status is automatically set to "Open"', run: async (ctx) => {
        const rec = await waitForRecord(ctx.workspaceId, 'sc1', (r) => r.values?.f2 === 'Open');
        return rec
          ? { ok: true, detail: 'Ticket status became "Open" automatically.' }
          : { ok: false, detail: 'Status did not change within 25s.' };
      } },
    ],
  },
];

// ── Sandbox lifecycle ─────────────────────────────────────────────────────────

// Reuse a single stable sandbox workspace so it shows up once in the workspace
// selector (where you can switch to it and inspect its Logs), rather than
// spawning a new one per run. Falls back to creating it if none exists.
export async function createSandbox() {
  const existing = (await dataService.getWorkspaces())
    .find((w) => (w.name || '').startsWith(SANDBOX_PREFIX));
  if (existing) return existing.id;
  return dataService.createWorkspace(SANDBOX_PREFIX, 'Disposable workspace for the in-app Test App. Safe to delete.');
}

// Tell the app shell to refresh its workspace list so the sandbox appears /
// disappears in the selector without a page reload. Called AFTER a run (not
// during it) and after cleanup, to avoid disrupting an in-flight run.
export function notifyWorkspacesChanged() {
  try { window.dispatchEvent(new CustomEvent('projectly_workspaces_changed')); } catch { /* SSR/no-window */ }
}

// Removes every sandbox workspace and the inbox notifications they produced.
// Returns a small summary of what was cleaned up.
export async function deleteAllTestData() {
  const workspaces = await dataService.getWorkspaces();
  const sandboxes = workspaces.filter((w) => (w.name || '').startsWith(SANDBOX_PREFIX));
  const sandboxIds = new Set(sandboxes.map((w) => w.id));

  let inboxRemoved = 0;
  for (const item of getInboxItems()) {
    if (sandboxIds.has(item.workspaceId)) {
      try { await deleteInboxItem(item.id); inboxRemoved++; } catch { /* best effort */ }
    }
  }
  for (const w of sandboxes) {
    try { await dataService.deleteWorkspace(w.id); } catch { /* best effort */ }
  }
  notifyWorkspacesChanged();
  return { workspacesRemoved: sandboxes.length, inboxRemoved };
}

// ── Report ───────────────────────────────────────────────────────────────────

export function buildReport(results, startedAt) {
  const lines = [];
  const passed = results.filter((r) => r.status === 'pass').length;
  const failed = results.filter((r) => r.status === 'fail').length;
  lines.push('PROJECTLY — IN-APP TEST REPORT');
  lines.push(`Run at: ${startedAt}`);
  lines.push(`Result: ${failed === 0 ? 'ALL PASSED' : `${failed} FAILED`} (${passed} passed, ${failed} failed)`);
  lines.push('');
  let lastJourney = null;
  for (const r of results) {
    if (r.journey !== lastJourney) {
      lines.push(`\n## ${r.journey}`);
      lastJourney = r.journey;
    }
    lines.push(`  ${r.status === 'pass' ? '[PASS]' : '[FAIL]'} ${r.label}${r.detail ? ` — ${r.detail}` : ''}`);
  }
  lines.push('');
  lines.push('(Tests ran in a disposable Test Sandbox workspace. Use "Delete all test data" to remove it.)');
  return lines.join('\n');
}
